/**
 * Horario operativo del agente (auto-respuesta) — PURO, compartido api+web.
 *
 * El motor (`ai-engine`) lo consulta antes de responder; el panel lo usa para
 * el indicador "Disponible / Fuera de horario" con la MISMA lógica (nunca dos
 * implementaciones que puedan divergir).
 *
 * Convenciones:
 *  - `endHour` es EXCLUSIVO: una ventana 7–22 cubre de 07:00:00 a 21:59:59.
 *  - 0–24 = siempre activo.
 *  - Soporta ventanas que cruzan medianoche (p. ej. 22–6).
 */

/** Hora (0–23) del instante `now` en la zona `timezone`. */
export function hourInTimezone(now: Date, timezone: string): number {
  const formatted = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(now);
  const n = Number(formatted);
  // Intl puede devolver "24" para la medianoche en algunos motores → normalizar a 0.
  return Number.isNaN(n) ? now.getHours() : n % 24;
}

/** ¿`now` cae dentro de la ventana operativa [startHour, endHour) en `timezone`? */
export function isWithinOperatingHours(
  now: Date,
  timezone: string,
  startHour: number,
  endHour: number,
): boolean {
  // Ventana completa: siempre activo (no hace falta calcular la hora).
  if (startHour <= 0 && endHour >= 24) return true;
  const h = hourInTimezone(now, timezone);
  if (startHour <= endHour) return h >= startHour && h < endHour;
  // Ventana que cruza la medianoche (p. ej. 22–6).
  return h >= startHour || h < endHour;
}

/**
 * Día de la semana (0 = domingo) y minuto del día en una zona horaria.
 *
 * Hace falta además de `hourInTimezone` porque el horario de Osito cambia por
 * día (el domingo no abre) y el sábado cierra a una hora exacta. Con solo la
 * hora no se puede saber si un sábado a las 18:00 la tienda está abierta.
 *
 * El servidor puede estar en UTC y el negocio en GMT-4: "hoy" no empieza a la
 * misma hora para los dos (es el aviso de B4 del CANON). Por eso todo se deriva
 * de `Intl` con la zona del negocio y nunca de `Date.getDay()` local.
 */
export function diaYMinutoEnZona(
  now: Date,
  timezone: string,
): { dia: number; hora: number; minuto: number } {
  const partes = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(now);

  const get = (tipo: string): string => partes.find((p) => p.type === tipo)?.value ?? '';

  const DIAS: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  const dia = DIAS[get('weekday')];
  const hora = Number(get('hour'));
  const minuto = Number(get('minute'));

  return {
    dia: dia ?? now.getDay(),
    hora: Number.isNaN(hora) ? now.getHours() : hora % 24,
    minuto: Number.isNaN(minuto) ? now.getMinutes() : minuto,
  };
}
